import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { useCANvisionContext } from '../../context/useCANvisionContext';

// Drawer components
import SiteManager from '../SiteManager/SiteManager';
import CameraSettings from '../CameraSettings/CameraSettings';
import CameraSetManager from '../CameraSetManager/CameraSetManager';
import { ChecklistMappingSettings } from '../ChecklistMappingSettings';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders all management drawers (Site Manager, Camera Settings, Camera Set
 * Manager, Checklist Mapping Settings) and wires their open/close state plus
 * data-change callbacks to the shared CANvision context.
 *
 * This component is designed to be composed into `<CANvisionDashboard>` in
 * Phase 3. For now it is exported standalone so it can be tested in isolation.
 *
 * Usage:
 * ```tsx
 * <CANvisionProvider>
 *   ...
 *   <DashboardDrawers />
 * </CANvisionProvider>
 * ```
 */
export function DashboardDrawers() {
  // -- Context ----------------------------------------------------------------
  const {
    // Sites data
    sites,
    selectedSiteId,
    refetchSites,

    // Cameras
    refetchCameras,

    // Drawer open/close state
    siteManagerOpen,
    setSiteManagerOpen,
    cameraSettingsOpen,
    setCameraSettingsOpen,
    cameraSetManagerOpen,
    setCameraSetManagerOpen,
    checklistMappingOpen,
    setChecklistMappingOpen,
  } = useCANvisionContext();

  // -- Redux (currentUserId for SiteManager's user-access feature) -----------
  const { userInfo } = useSelector(selectUserInfo);
  const currentUserId = userInfo?.userId;

  // -- Derived values --------------------------------------------------------
  const selectedSite = sites.find((s) => s.id === selectedSiteId) ?? null;

  // -- Handlers (drawer close + data-change callbacks) -----------------------

  const handleCloseSiteManager = useCallback(() => {
    setSiteManagerOpen(false);
  }, [setSiteManagerOpen]);

  const handleSitesChanged = useCallback(() => {
    refetchSites();
  }, [refetchSites]);

  const handleCloseCameraSettings = useCallback(() => {
    setCameraSettingsOpen(false);
  }, [setCameraSettingsOpen]);

  const handleCamerasChanged = useCallback(() => {
    refetchCameras();
  }, [refetchCameras]);

  const handleCloseCameraSetManager = useCallback(() => {
    setCameraSetManagerOpen(false);
  }, [setCameraSetManagerOpen]);

  /**
   * CameraSetManager manages its own data via the `useCameraSets` hook
   * internally. The `onSetsChanged` callback is provided so external
   * consumers can react to set mutations (e.g. the Video Wall rotation
   * list in the old index.jsx). Here we trigger a cameras refetch as a
   * reasonable side-effect in case set membership changed camera state.
   */
  const handleSetsChanged = useCallback(() => {
    refetchCameras();
  }, [refetchCameras]);

  const handleCloseChecklistMapping = useCallback(() => {
    setChecklistMappingOpen(false);
  }, [setChecklistMappingOpen]);

  // -- Render ----------------------------------------------------------------

  return (
    <>
      {/* Site Management drawer */}
      <SiteManager
        open={siteManagerOpen}
        onClose={handleCloseSiteManager}
        onSitesChanged={handleSitesChanged}
        currentUserId={currentUserId}
      />

      {/* Camera Settings drawer */}
      <CameraSettings
        open={cameraSettingsOpen}
        onClose={handleCloseCameraSettings}
        siteId={selectedSiteId}
        siteName={selectedSite?.display_name || selectedSite?.name}
        onCamerasChanged={handleCamerasChanged}
      />

      {/* Camera Set Manager drawer */}
      <CameraSetManager
        open={cameraSetManagerOpen}
        onClose={handleCloseCameraSetManager}
        onSetsChanged={handleSetsChanged}
      />

      {/* Checklist Mapping Settings drawer */}
      <ChecklistMappingSettings
        open={checklistMappingOpen}
        onClose={handleCloseChecklistMapping}
      />
    </>
  );
}

export default DashboardDrawers;
